import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { MloBulkAssignCard } from '../components/admin/MloOfRecord';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const ROLE_OPTIONS = [
  { value: 'admin',           label: 'Admin' },
  { value: 'account_manager', label: 'Account Manager' },
  { value: 'contract_desk',   label: 'Real Estate Desk' },
  { value: 'loan_officer',    label: 'Loan Officer' },
  { value: 'client_success',  label: 'Client Success' },
  // Partner Success is PARTNER-side. It is default-closed against borrower
  // files server-side (constants/staffRoles.js BORROWER_DATA_ROLES) — this
  // picker only makes the role assignable, it does not grant anything.
  { value: 'partner_success', label: 'Partner Success' },
];
const roleLabel = (r) => ROLE_OPTIONS.find((o) => o.value === r)?.label || r;
const rolePill = (r) => {
  const isAdmin = r === 'admin';
  return {
    background: isAdmin ? '#3a6ad9' : '#f3f4f6',
    color:      isAdmin ? '#fff'    : '#374151',
  };
};

/**
 * /admin/settings/users — staff user management. Admin-only (gated by
 * requireSuperAdmin server-side; the route guard in App.js redirects
 * non-admins to /admin/borrowers with a toast).
 */
export default function AdminSettingsUsers() {
  const navigate = useNavigate();
  const token = typeof window !== 'undefined' ? localStorage.getItem('doorly_admin_token') : null;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const me = (() => {
    try { return JSON.parse(localStorage.getItem('doorly_admin_user') || '{}'); } catch { return {}; }
  })();

  const [staff, setStaff]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [err, setErr]           = useState('');
  const [toast, setToast]       = useState(null);

  // Invite modal state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', role: 'loan_officer', title: '', nmls_number: '', reason: '' });
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteErr, setInviteErr] = useState('');

  // Edit modal state — combined Name + Role editor.
  const [editTarget, setEditTarget] = useState(null);
  const [editName, setEditName]     = useState('');
  const [editRole, setEditRole]     = useState('admin');
  const [editTitle, setEditTitle]   = useState('');
  const [editNmls, setEditNmls]     = useState('');
  // 🚨 A ROLE CHANGE NEEDS A REASON (hotfix-priv, 2026-11-01): the server refuses one without it and audit-logs it.
  const [editReason, setEditReason] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editErr, setEditErr]       = useState('');

  // Reset Password modal state — admin sets another staff user's password.
  const [resetTarget, setResetTarget]     = useState(null);
  const [resetPw, setResetPw]             = useState('');
  const [resetPwc, setResetPwc]           = useState('');
  const [showResetPw, setShowResetPw]     = useState(false);
  const [showResetPwc, setShowResetPwc]   = useState(false);
  const [resetSaving, setResetSaving]     = useState(false);
  const [resetErr, setResetErr]           = useState('');

  // Deactivate confirmation dialog — same UI for "Deactivate" and
  // "Cancel Invite" (both flip is_active=false). The button label that
  // opened the dialog drives the copy.
  const [deactTarget, setDeactTarget]     = useState(null); // { id, name, label } | null
  const [deactSaving, setDeactSaving]     = useState(false);
  // Removal-flow state. Only used for a real Deactivate — cancelling a pending
  // invite has no impact to preview and no termination date to record.
  const [deactImpact, setDeactImpact]     = useState(null);   // null while loading
  const [deactSuccessor, setDeactSuccessor] = useState('');
  const [deactDate, setDeactDate]         = useState('');
  const [deactErr, setDeactErr]           = useState('');

  // ── 🚨 THE DEPLOY GATE SAYS "1 SWALLOWED" AND NOTHING SAYS WHICH ────────────
  //
  // Jesse, four deploys running: "Deployed <sha> with 1 swallowed boot
  // statements, expected at most 0."
  //
  // 🔑 THE LABEL HAS EXISTED THE WHOLE TIME AND HAD NOWHERE TO BE READ. The boot
  // prints it, but that means log-diving on Render; and
  // GET /api/admin/schema-boot-failures needs a Bearer token, so it cannot be
  // opened in a browser tab. Four rounds of "please paste the line" is the
  // symptom of a diagnostic with no surface — so it gets one, here, on the
  // super-admin page.
  //
  // ⚠️ LABELS AND SQLSTATEs ONLY, which is all the endpoint serves: statement
  // identifiers and error codes, never a parameter, a message or a value. That
  // is also why it sits on the super-admin page rather than a wider one — the
  // list is an inventory of the schema's weak points, the same reasoning that
  // keeps it off the public /api/health.
  //
  // ⚠️ COUNTS ARE FOR ONE INSTANCE. Render may run several and each boots its
  // own, so a reload can legitimately show a different number. The panel says so
  // rather than letting one poll read as the whole fleet.
  const [boot, setBoot] = useState(null);
  const [bootErr, setBootErr] = useState('');
  const fetchBoot = useCallback(async () => {
    setBootErr('');
    try {
      const r = await axios.get(`${API}/api/admin/schema-boot-failures`, { headers });
      setBoot(r.data || null);
    } catch (e) {
      setBootErr(e.response?.data?.error || e.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStaff = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await axios.get(`${API}/api/admin/staff`, { headers });
      setStaff(r.data.staff || []);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
      if (e.response?.status === 403) {
        setTimeout(() => navigate('/admin/borrowers', { replace: true }), 1500);
      }
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    if (!token) { navigate('/admin/login', { replace: true }); return; }
    fetchStaff();
    // 🔑 THE SAME PAGE LOAD ANSWERS BOTH "who is on the team" and "did this
    // instance boot cleanly" — a diagnostic nobody opens is a diagnostic nobody
    // reads, which is how four deploys went by with the label unread.
    fetchBoot();
  }, [token, fetchStaff, fetchBoot, navigate]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 4000); };

  const sendInvite = async () => {
    setInviteErr('');
    if (!inviteForm.name.trim() || !inviteForm.email.trim()) {
      setInviteErr('Name and email are required.'); return;
    }
    if (!inviteForm.title.trim()) {
      setInviteErr('Title is required.'); return;
    }
    if (inviteForm.role === 'loan_officer' && !inviteForm.nmls_number.trim()) {
      setInviteErr('NMLS number is required for Loan Officers.'); return;
    }
    if (!inviteForm.reason.trim()) {
      setInviteErr('A reason is required to grant a staff role.'); return;
    }
    setInviteSending(true);
    const url = `${API}/api/admin/staff/invite`;
    try {
      console.log('[StaffInvite] POST', url, { payload: inviteForm, hasToken: !!token });
      const r = await axios.post(url, inviteForm, { headers });
      console.log('[StaffInvite] success', { status: r.status, data: r.data });
      showToast(`Invite sent to ${inviteForm.email}! Link is valid for 72 hours.`);
      setInviteOpen(false);
      setInviteForm({ name: '', email: '', role: 'loan_officer', title: '', nmls_number: '', reason: '' });
      fetchStaff();
    } catch (e) {
      console.error('[StaffInvite] FAILED', {
        url,
        status: e.response?.status,
        statusText: e.response?.statusText,
        data: e.response?.data,
        message: e.message,
        code: e.code,
      });
      setInviteErr(e.response?.data?.error || `${e.response?.status || ''} ${e.message}`.trim());
    } finally { setInviteSending(false); }
  };

  const resendInvite = async (id, email) => {
    const url = `${API}/api/admin/staff/${id}/resend-invite`;
    try {
      console.log('[StaffInvite] POST resend', url);
      const r = await axios.post(url, {}, { headers });
      console.log('[StaffInvite] resend success', { status: r.status, data: r.data });
      showToast(`Invite resent to ${email}.`);
      fetchStaff();
    } catch (e) {
      console.error('[StaffInvite] resend FAILED', {
        url,
        status: e.response?.status,
        statusText: e.response?.statusText,
        data: e.response?.data,
        message: e.message,
      });
      alert('Resend failed: ' + (e.response?.data?.error || e.message));
    }
  };

  // ⚠️ THE CONFIRM DIALOG LOADS THE CONSEQUENCES BEFORE IT WILL LET YOU CONFIRM.
  // Removing a rep hands their partners and open tickets to someone else; an
  // admin agreeing to that without being told how many of each is agreeing to a
  // number they cannot see.
  const openDeactivate = async (s) => {
    const isInvite = !s.invite_accepted;
    setDeactTarget({ id: s.id, name: s.name || s.email, label: isInvite ? 'Cancel Invite' : 'Deactivate' });
    setDeactSuccessor(''); setDeactErr(''); setDeactImpact(null);
    // Default the effective date to today, in local time — a date input wants
    // YYYY-MM-DD and toISOString() would shift it a day for anyone west of UTC.
    const d = new Date();
    setDeactDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    if (isInvite) { setDeactImpact({ skip: true }); return; }
    try {
      const r = await axios.get(`${API}/api/admin/staff/${s.id}/removal-impact`, { headers });
      setDeactImpact(r.data);
    } catch (e) {
      // ⚠️ FAILING TO LOAD THE IMPACT MUST NOT LOOK LIKE "NOTHING TO HAND OVER".
      // An empty preview and an unavailable preview are the same screen unless
      // they are deliberately made different, and the first one authorises a
      // removal the admin has not actually been shown.
      setDeactImpact({ error: e.response?.data?.error || e.message });
    }
  };

  const setActive = async (id, active) => {
    try {
      await axios.patch(`${API}/api/admin/staff/${id}`, { is_active: active }, { headers });
      showToast(active ? 'User reactivated.' : 'User deactivated.');
      fetchStaff();
    } catch (e) {
      alert('Update failed: ' + (e.response?.data?.error || e.message));
    }
  };

  // Save name + role together. Backend PATCH already accepts both fields
  // and splits name on the first whitespace into first_name / last_name.
  const saveEdit = async () => {
    if (!editTarget) return;
    setEditErr('');
    const cleanName = editName.trim();
    if (!cleanName) { setEditErr('Name is required.'); return; }
    if (!editTitle.trim()) { setEditErr('Title is required.'); return; }
    if (editRole === 'loan_officer' && !editNmls.trim()) {
      setEditErr('NMLS number is required for Loan Officers.'); return;
    }
    setEditSaving(true);
    try {
      const body = {};
      const originalName = `${editTarget.first_name || ''} ${editTarget.last_name || ''}`.trim() || editTarget.name || '';
      if (cleanName !== originalName) body.name = cleanName;
      if (editRole !== editTarget.role) {
        if (!editReason.trim()) { setEditErr('A reason is required to change a role.'); return; }
        body.role = editRole;
        body.reason = editReason.trim();
      }
      if (editTitle.trim() !== (editTarget.title || '')) body.title = editTitle.trim();
      if (editNmls.trim() !== (editTarget.nmls_number || '')) body.nmls_number = editNmls.trim();
      if (Object.keys(body).length === 0) {
        setEditTarget(null);
        return;
      }
      await axios.patch(`${API}/api/admin/staff/${editTarget.id}`, body, { headers });
      showToast(`Changes saved for ${cleanName}.`);
      setEditTarget(null);
      fetchStaff();
    } catch (e) {
      setEditErr(e.response?.data?.error || e.message);
    } finally { setEditSaving(false); }
  };

  // Admin sets another staff user's password directly. Validates client-side
  // for instant feedback; backend re-validates the same rules.
  const submitResetPassword = async () => {
    if (!resetTarget) return;
    setResetErr('');
    if (resetPw.length < 8 || !/\d/.test(resetPw) || !/[A-Z]/.test(resetPw)) {
      setResetErr('Password must be at least 8 characters and include a number and an uppercase letter.');
      return;
    }
    if (resetPw !== resetPwc) { setResetErr('Passwords do not match.'); return; }
    setResetSaving(true);
    try {
      await axios.post(`${API}/api/admin/staff/${resetTarget.id}/reset-password`,
        { newPassword: resetPw }, { headers });
      showToast(`Password updated for ${resetTarget.email}.`);
      setResetTarget(null);
      setResetPw(''); setResetPwc(''); setShowResetPw(false); setShowResetPwc(false);
    } catch (e) {
      setResetErr(e.response?.data?.error || e.message);
    } finally { setResetSaving(false); }
  };

  // ⚠️ DEACTIVATE AND CANCEL INVITE ARE NOW DIFFERENT REQUESTS, BECAUSE THEY ARE
  // DIFFERENT EVENTS. They used to share one PATCH is_active=false, which is how
  // a real departure came to be recorded the same way as an invite nobody
  // accepted: no handover, no termination date, and partners still listed as
  // covered by someone who could no longer log in.
  //
  //   • Cancel Invite → PATCH. Nobody started, so nothing to hand over and no
  //     termination to date. The server allows is_active:false only for this case.
  //   • Deactivate    → DELETE /staff/:id, which moves partners and open tickets
  //     in one transaction and stamps terminated_at. The server now REFUSES the
  //     PATCH for anyone who accepted their invite, so this is not a preference.
  const submitDeactivate = async () => {
    if (!deactTarget) return;
    setDeactSaving(true); setDeactErr('');
    try {
      if (deactTarget.label === 'Cancel Invite') {
        await axios.patch(`${API}/api/admin/staff/${deactTarget.id}`, { is_active: false }, { headers });
        showToast(`${deactTarget.name || 'User'} invite cancelled.`);
      } else {
        const body = {};
        if (deactSuccessor) body.reassign_to = Number(deactSuccessor);
        // Sent as a date, not a timestamp: the effective date is what the admin
        // typed, and attaching a local time to it is precision we do not have.
        if (deactDate) body.terminated_at = deactDate;
        const r = await axios.delete(`${API}/api/admin/staff/${deactTarget.id}`, { headers, data: body });
        const m = r.data?.moved || {};
        // ⚠️ SAY WHAT MOVED, not "done". The counts are the only confirmation the
        // admin gets that the handover they authorised is the one that happened.
        showToast(`${deactTarget.name || 'User'} removed — ${m.partners ?? 0} partner(s), ${m.openTickets ?? 0} open ticket(s) moved.`);
      }
      setDeactTarget(null);
      fetchStaff();
    } catch (e) {
      const d = e.response?.data;
      if (d?.reason === 'would_orphan') {
        // The server refused because nobody was named. Keep the dialog open with
        // the impact it returned rather than dropping the admin back to the list.
        setDeactImpact({ ...(d.impact || {}), successors: deactImpact?.successors || [] });
        setDeactErr('Choose who takes over before removing this person.');
      } else {
        setDeactErr(d?.error || e.message);
      }
    } finally { setDeactSaving(false); }
  };

  const fmtDate = (v) => {
    if (!v) return null;
    return new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const statusPill = (s) => {
    // An inactive row that carries a termination date says WHEN — the date the
    // commission tail counts from, so it belongs where someone can check it.
    if (!s.is_active) {
      return { label: s.terminated_at ? `Left ${fmtDate(s.terminated_at)}` : 'Inactive', bg: '#f3f4f6', color: '#6b7280' };
    }
    if (!s.invite_accepted) return { label: 'Invite Pending', bg: '#fef3c7', color: '#92400e' };
    return { label: 'Active', bg: '#d4f99a', color: '#15803d' };
  };

  return (
    <AdminLayout>
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '24px 32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#111827' }}>Team Members</h1>
          <button onClick={() => setInviteOpen(true)} type="button"
            style={{ padding: '9px 18px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            + Invite User
          </button>
        </div>

        {/* 🔑 Open applications with no MLO of record, and the allow-listed bulk assign (push B, 2026-10-31). */}
        {token && <MloBulkAssignCard token={token} />}

        {/* 🚨 SCHEMA BOOT — green when clean, and it NAMES the statement when not. */}
        {boot && (
          <div style={{
            padding: '12px 14px', borderRadius: 8, fontSize: 12.5, marginBottom: 16,
            background: boot.swallowed ? '#fff0f3' : '#f0fdf4',
            border: `1px solid ${boot.swallowed ? '#ffc5ca' : '#bbf7d0'}`,
            color: '#111827',
          }}>
            <div style={{ fontWeight: 700, marginBottom: boot.swallowed ? 8 : 0 }}>
              Schema boot — {boot.swallowed} swallowed, {boot.tolerated} tolerated
              {!boot.swallowed && ' \u2713'}
            </div>
            {boot.swallowed > 0 && (
              <>
                <div style={{ marginBottom: 6, color: '#6b7280' }}>
                  These statements failed during this instance&apos;s boot and the error was
                  discarded. The deploy gate fails on any of them.
                </div>
                <ul style={{ margin: '0 0 8px 18px', padding: 0 }}>
                  {(boot.swallowed_statements || []).map((e, i) => (
                    <li key={i} style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
                      {e.label} <span style={{ color: '#6b7280' }}>({e.code || 'no SQLSTATE'})</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {/* ⚠️ ONE INSTANCE, NOT THE FLEET — the endpoint says so and so does this. */}
            <div style={{ color: '#6b7280', fontSize: 11.5 }}>
              Counts are for the instance that served this request. Re-check to sample another.
              {' '}
              <button type="button" onClick={fetchBoot}
                style={{ background: 'none', border: 'none', padding: 0, color: '#3a6ad9', cursor: 'pointer', fontSize: 11.5, textDecoration: 'underline' }}>
                re-check
              </button>
            </div>
          </div>
        )}
        {bootErr && (
          <div style={{ padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: 8, fontSize: 12.5, marginBottom: 16 }}>
            Could not read the schema boot record: {bootErr}
          </div>
        )}

        {err && (
          <div style={{ padding: '10px 14px', background: '#fff0f3', border: '1px solid #ffc5ca', color: '#ff082f', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
            {err}
          </div>
        )}

        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                {['Name','Email','Role','Status','Last Login','Invited','Actions'].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: 11, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.04 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ padding: 24, color: '#9ca3af', textAlign: 'center' }}>Loading…</td></tr>
              ) : staff.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 24, color: '#9ca3af', textAlign: 'center' }}>No staff users yet.</td></tr>
              ) : staff.map((s) => {
                const isMe = s.id === me?.id;
                const sp = statusPill(s);
                const rp = rolePill(s.role);
                return (
                  <tr key={s.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '12px 14px', fontWeight: 600, color: '#111827' }}>
                      {s.name || '—'} {isMe && <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 500 }}>(you)</span>}
                    </td>
                    <td style={{ padding: '12px 14px', color: '#374151' }}>{s.email}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ ...rp, padding: '3px 9px', borderRadius: 9999, fontSize: 11, fontWeight: 700 }}>{roleLabel(s.role)}</span>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ background: sp.bg, color: sp.color, padding: '3px 9px', borderRadius: 9999, fontSize: 11, fontWeight: 700 }}>{sp.label}</span>
                    </td>
                    <td style={{ padding: '12px 14px', color: '#6b7280' }}>{fmtDate(s.last_login_at) || 'Never'}</td>
                    <td style={{ padding: '12px 14px', color: '#6b7280' }}>{fmtDate(s.invited_at) || '—'}</td>
                    <td style={{ padding: '12px 14px' }}>
                      {isMe ? (
                        <span title="You cannot modify your own account" style={{ fontSize: 11, color: '#d1d5db' }}>—</span>
                      ) : (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {!s.is_active ? (
                            <button onClick={() => setActive(s.id, true)} type="button"
                              style={{ padding: '4px 10px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                              Reactivate
                            </button>
                          ) : !s.invite_accepted ? (
                            <>
                              <button onClick={() => resendInvite(s.id, s.email)} type="button"
                                style={{ padding: '4px 10px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                Resend Invite
                              </button>
                              <button onClick={() => openDeactivate(s)} type="button"
                                style={{ padding: '4px 10px', background: '#fff', color: '#ff082f', border: '1px solid #ffc5ca', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                Cancel Invite
                              </button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => {
                                  setEditTarget(s);
                                  setEditName(s.name || `${s.first_name || ''} ${s.last_name || ''}`.trim());
                                  setEditRole(s.role);
                                  setEditTitle(s.title || '');
                                  setEditNmls(s.nmls_number || '');
                                  setEditErr(''); setEditReason('');
                                }} type="button"
                                style={{ padding: '4px 10px', background: '#fff', color: '#3a6ad9', border: '1px solid #bfdbfe', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                Edit
                              </button>
                              <button onClick={() => {
                                  setResetTarget({ id: s.id, email: s.email, name: s.name });
                                  setResetPw(''); setResetPwc(''); setShowResetPw(false); setShowResetPwc(false); setResetErr('');
                                }} type="button"
                                style={{ padding: '4px 10px', background: '#fff', color: '#3a6ad9', border: '1px solid #bfdbfe', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                Reset Password
                              </button>
                              <button onClick={() => openDeactivate(s)} type="button"
                                style={{ padding: '4px 10px', background: '#fff', color: '#ff082f', border: '1px solid #ffc5ca', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                Deactivate
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Invite modal */}
        {inviteOpen && (
          <div onClick={() => !inviteSending && setInviteOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 9999 }}>
            <div onClick={(e) => e.stopPropagation()}
              style={{ background: '#fff', borderRadius: 14, maxWidth: 460, width: '100%', boxShadow: '0 20px 50px rgba(0,0,0,0.3)', padding: 28 }}>
              <h2 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 800, color: '#111827' }}>Invite User</h2>
              <p style={{ margin: '0 0 18px', fontSize: 13, color: '#6b7280' }}>They'll receive a link valid for 72 hours.</p>

              <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.04, display: 'block', marginBottom: 4 }}>Full Name</label>
              <input type="text" value={inviteForm.name} onChange={(e) => setInviteForm((p) => ({ ...p, name: e.target.value }))}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', marginBottom: 12 }} />

              <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.04, display: 'block', marginBottom: 4 }}>Email</label>
              <input type="email" value={inviteForm.email} onChange={(e) => setInviteForm((p) => ({ ...p, email: e.target.value.trim() }))}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', marginBottom: 12 }} />

              <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.04, display: 'block', marginBottom: 4 }}>Role</label>
              <select value={inviteForm.role} onChange={(e) => setInviteForm((p) => ({ ...p, role: e.target.value }))}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', marginBottom: 14, background: '#fff' }}>
                {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>

              <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.04, display: 'block', marginBottom: 4 }}>Title <span style={{ color: '#ff082f' }}>*</span></label>
              <input type="text" value={inviteForm.title} onChange={(e) => setInviteForm((p) => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Account Manager, Loan Officer"
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', marginBottom: 12 }} />

              {inviteForm.role === 'loan_officer' && (
                <>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.04, display: 'block', marginBottom: 4 }}>NMLS # <span style={{ color: '#ff082f' }}>*</span></label>
                  <input type="text" value={inviteForm.nmls_number} onChange={(e) => setInviteForm((p) => ({ ...p, nmls_number: e.target.value }))}
                    placeholder="e.g. 2764749"
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', marginBottom: 14 }} />
                </>
              )}

              <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.04, display: 'block', marginBottom: 4 }}>Reason <span style={{ color: '#ff082f' }}>*</span></label>
              <input type="text" value={inviteForm.reason} onChange={(e) => setInviteForm((p) => ({ ...p, reason: e.target.value }))}
                placeholder="Why this person needs this role (audit-logged)"
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', marginBottom: 12 }} />

              {inviteErr && (
                <div style={{ marginBottom: 12, padding: '10px 12px', background: '#fff0f3', border: '1px solid #ffc5ca', color: '#ff082f', borderRadius: 8, fontSize: 13 }}>
                  {inviteErr}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setInviteOpen(false)} disabled={inviteSending} type="button"
                  style={{ padding: '9px 16px', background: 'transparent', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  Cancel
                </button>
                <button onClick={sendInvite} disabled={inviteSending} type="button"
                  style={{ padding: '9px 18px', background: inviteSending ? '#93c5fd' : '#3a6ad9', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: inviteSending ? 'wait' : 'pointer' }}>
                  {inviteSending ? 'Sending…' : 'Send Invite'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit modal — Name + Email (read-only) + Role */}
        {editTarget && (
          <div onClick={() => !editSaving && setEditTarget(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 9999 }}>
            <div onClick={(e) => e.stopPropagation()}
              style={{ background: '#fff', borderRadius: 14, maxWidth: 460, width: '100%', boxShadow: '0 20px 50px rgba(0,0,0,0.3)', padding: 28 }}>
              <h2 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 800, color: '#111827' }}>Edit User</h2>
              <p style={{ margin: '0 0 18px', fontSize: 13, color: '#6b7280' }}>Update name and role. Email cannot be changed.</p>

              <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.04, display: 'block', marginBottom: 4 }}>Full Name</label>
              <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', marginBottom: 12 }} />

              <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.04, display: 'block', marginBottom: 4 }}>Email</label>
              <input type="email" value={editTarget.email || ''} readOnly
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #e5e7eb', background: '#f9fafb', color: '#6b7280', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', marginBottom: 12 }} />

              <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.04, display: 'block', marginBottom: 4 }}>Role</label>
              <select value={editRole} onChange={(e) => setEditRole(e.target.value)}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', marginBottom: 14, background: '#fff' }}>
                {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>

              <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.04, display: 'block', marginBottom: 4 }}>Title <span style={{ color: '#ff082f' }}>*</span></label>
              <input type="text" value={editTitle} onChange={(e) => setEditTitle(e.target.value)}
                placeholder="e.g. Account Manager, Loan Officer"
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', marginBottom: 12 }} />

              {editRole === 'loan_officer' && (
                <>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.04, display: 'block', marginBottom: 4 }}>NMLS # <span style={{ color: '#ff082f' }}>*</span></label>
                  <input type="text" value={editNmls} onChange={(e) => setEditNmls(e.target.value)}
                    placeholder="e.g. 2764749"
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', marginBottom: 14 }} />
                </>
              )}

              {editRole !== editTarget.role && (
                <>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.04, display: 'block', marginBottom: 4 }}>Reason for the role change <span style={{ color: '#ff082f' }}>*</span></label>
                  <input type="text" value={editReason} onChange={(e) => setEditReason(e.target.value)}
                    placeholder="Audit-logged"
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', marginBottom: 12 }} />
                </>
              )}

              {editErr && (
                <div style={{ marginBottom: 12, padding: '10px 12px', background: '#fff0f3', border: '1px solid #ffc5ca', color: '#ff082f', borderRadius: 8, fontSize: 13 }}>
                  {editErr}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setEditTarget(null)} disabled={editSaving} type="button"
                  style={{ padding: '9px 16px', background: 'transparent', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  Cancel
                </button>
                <button onClick={saveEdit} disabled={editSaving} type="button"
                  style={{ padding: '9px 18px', background: editSaving ? '#93c5fd' : '#3a6ad9', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: editSaving ? 'wait' : 'pointer' }}>
                  {editSaving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reset Password modal */}
        {resetTarget && (
          <div onClick={() => !resetSaving && setResetTarget(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 9999 }}>
            <div onClick={(e) => e.stopPropagation()}
              style={{ background: '#fff', borderRadius: 14, maxWidth: 460, width: '100%', boxShadow: '0 20px 50px rgba(0,0,0,0.3)', padding: 28 }}>
              <h2 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 800, color: '#111827' }}>Reset Password</h2>
              <p style={{ margin: '0 0 18px', fontSize: 13, color: '#6b7280' }}>{resetTarget.name || 'User'} ({resetTarget.email})</p>

              <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.04, display: 'block', marginBottom: 4 }}>New Password</label>
              <div style={{ position: 'relative', marginBottom: 12 }}>
                <input type={showResetPw ? 'text' : 'password'} value={resetPw} onChange={(e) => setResetPw(e.target.value)}
                  style={{ width: '100%', padding: '9px 38px 9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                <button type="button" onClick={() => setShowResetPw((v) => !v)}
                  style={{ position: 'absolute', right: 10, top: 0, height: '100%', background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 11 }}>
                  {showResetPw ? 'Hide' : 'Show'}
                </button>
              </div>

              <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.04, display: 'block', marginBottom: 4 }}>Confirm Password</label>
              <div style={{ position: 'relative', marginBottom: 6 }}>
                <input type={showResetPwc ? 'text' : 'password'} value={resetPwc} onChange={(e) => setResetPwc(e.target.value)}
                  style={{ width: '100%', padding: '9px 38px 9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                <button type="button" onClick={() => setShowResetPwc((v) => !v)}
                  style={{ position: 'absolute', right: 10, top: 0, height: '100%', background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 11 }}>
                  {showResetPwc ? 'Hide' : 'Show'}
                </button>
              </div>
              <p style={{ margin: '4px 0 14px', fontSize: 11, color: '#9ca3af' }}>At least 8 characters, 1 uppercase letter, 1 number.</p>

              {resetErr && (
                <div style={{ marginBottom: 12, padding: '10px 12px', background: '#fff0f3', border: '1px solid #ffc5ca', color: '#ff082f', borderRadius: 8, fontSize: 13 }}>
                  {resetErr}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setResetTarget(null)} disabled={resetSaving} type="button"
                  style={{ padding: '9px 16px', background: 'transparent', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  Cancel
                </button>
                <button onClick={submitResetPassword} disabled={resetSaving} type="button"
                  style={{ padding: '9px 18px', background: resetSaving ? '#93c5fd' : '#3a6ad9', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: resetSaving ? 'wait' : 'pointer' }}>
                  {resetSaving ? 'Setting…' : 'Set Password'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Deactivate / Cancel Invite confirm */}
        {deactTarget && (
          <div onClick={() => !deactSaving && setDeactTarget(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 9999 }}>
            <div onClick={(e) => e.stopPropagation()}
              style={{ background: '#fff', borderRadius: 14, maxWidth: 440, width: '100%', boxShadow: '0 20px 50px rgba(0,0,0,0.3)', padding: 28 }}>
              <h2 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 800, color: '#111827' }}>{deactTarget.label}?</h2>
              <p style={{ margin: '0 0 18px', fontSize: 14, color: '#374151', lineHeight: 1.6 }}>
                {deactTarget.label === 'Cancel Invite'
                  ? `Are you sure you want to cancel ${deactTarget.name}'s invite? They will no longer be able to activate their account using the link they received.`
                  : `${deactTarget.name} will lose access immediately. Everything they own moves to whoever takes over.`}
              </p>

              {/* ── What the removal actually moves ──────────────────────────
                  ⚠️ THREE STATES, KEPT APART ON PURPOSE. "Still loading",
                  "nothing to hand over" and "we could not find out" all render
                  as an empty area unless they are made to look different, and
                  the last two are the ones that matter: an unavailable preview
                  showing as "nothing to move" is how an admin authorises a
                  handover they were never shown. */}
              {deactTarget.label === 'Deactivate' && (
                <div style={{ marginBottom: 18 }}>
                  {deactImpact === null ? (
                    <div style={{ fontSize: 13, color: '#6b7280' }}>Checking what this moves…</div>
                  ) : deactImpact.error ? (
                    <div style={{ fontSize: 13, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 14px', lineHeight: 1.6 }}>
                      Couldn’t load what this removal moves ({deactImpact.error}). That is not the same as
                      nothing to move — reload before continuing.
                    </div>
                  ) : (deactImpact.partnerCount || deactImpact.openTicketCount) ? (
                    <div style={{ fontSize: 13, color: '#374151', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', lineHeight: 1.7 }}>
                      <strong>{deactImpact.partnerCount}</strong> partner account{deactImpact.partnerCount === 1 ? '' : 's'} and{' '}
                      <strong>{deactImpact.openTicketCount}</strong> open ticket{deactImpact.openTicketCount === 1 ? '' : 's'} move to the person you choose.
                      {deactImpact.resolvedTicketCount > 0 && (
                        <div style={{ color: '#6b7280', marginTop: 4 }}>
                          {deactImpact.resolvedTicketCount} resolved ticket{deactImpact.resolvedTicketCount === 1 ? '' : 's'} stay with {deactTarget.name} as history.
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ fontSize: 13, color: '#6b7280', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px' }}>
                      No partner accounts or open tickets to hand over.
                    </div>
                  )}
                </div>
              )}

              {/* Successor — only asked for when there is something to hand over. */}
              {deactTarget.label === 'Deactivate' && deactImpact && !deactImpact.error
                && (deactImpact.partnerCount > 0 || deactImpact.openTicketCount > 0) && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
                    Takes over their accounts
                  </label>
                  <select value={deactSuccessor} onChange={(e) => setDeactSuccessor(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, background: '#fff', color: '#111827' }}>
                    <option value="">Select someone…</option>
                    {(deactImpact.successors || []).map((u) => (
                      <option key={u.id} value={u.id}>{`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}</option>
                    ))}
                  </select>
                  {/* ⚠️ The one case the server lets through unowned: the last
                      holder of the role. Named here so an admin who sees no
                      options understands why, rather than assuming it is broken. */}
                  {deactImpact.canProceedWithoutSuccessor && (
                    <div style={{ fontSize: 12, color: '#92400e', marginTop: 6, lineHeight: 1.6 }}>
                      {deactTarget.name} is the last Partner Success holder. Leaving this blank marks these
                      accounts as held by nobody — recorded explicitly, not left blank.
                    </div>
                  )}
                </div>
              )}

              {/* ── Effective termination date ───────────────────────────────
                  ⚠️ NOT COSMETIC, AND NOT THE CLICK DATE. A rep earns on closings
                  from their accounts for up to 30 days after termination, and
                  this is the date that window counts from. Removals get actioned
                  in DoorlyDesk well after the fact, so it is editable. */}
              {deactTarget.label === 'Deactivate' && (
                <div style={{ marginBottom: 18 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
                    Last day
                  </label>
                  <input type="date" value={deactDate} onChange={(e) => setDeactDate(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, color: '#111827' }} />
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6, lineHeight: 1.6 }}>
                    Their actual last day, not today. Commission owed after a departure is calculated from it.
                  </div>
                </div>
              )}

              {deactErr && (
                <div style={{ fontSize: 13, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', marginBottom: 16, lineHeight: 1.6 }}>
                  {deactErr}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setDeactTarget(null)} disabled={deactSaving} type="button"
                  style={{ padding: '9px 16px', background: 'transparent', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  Cancel
                </button>
                <button onClick={submitDeactivate}
                  // ⚠️ BLOCKED WHILE THE IMPACT IS UNKNOWN — loading OR failed.
                  // The whole point of the preview is that the admin sees what
                  // they are authorising; a confirm button live before it lands
                  // makes the preview decorative.
                  disabled={deactSaving || (deactTarget.label === 'Deactivate' && (deactImpact === null || !!deactImpact.error))}
                  type="button"
                  style={{ padding: '9px 18px', background: (deactSaving || (deactTarget.label === 'Deactivate' && (deactImpact === null || !!deactImpact.error))) ? '#fda4af' : '#ff082f', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: deactSaving ? 'wait' : 'pointer' }}>
                  {deactSaving ? 'Working…' : deactTarget.label}
                </button>
              </div>
            </div>
          </div>
        )}

        {toast && (
          <div style={{ position: 'fixed', bottom: 'calc(24px + env(safe-area-inset-bottom, 0px))', right: 24, padding: '12px 20px', background: '#111827', color: '#acf44e', borderRadius: 8, fontSize: 13, fontWeight: 700, boxShadow: '0 4px 16px rgba(0,0,0,0.3)', zIndex: 9999 }}>
            {toast}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
